/**
 * Update gate (FE-04 FR-FE04-12): the "new version" banner and any reload wait while drive mode is
 * active, the editor has unsaved operations, a form is dirty or the outbox is flushing.
 *
 * Screens register a blocker with `useBlockUpdates(active, reason)` (or `blockUpdates(reason)` outside
 * React); the banner reads `isUpdateBlocked()` through `useUpdateBlocked()`.
 */
import { useEffect, useSyncExternalStore } from "react";

export type UpdateBlocker = "drive" | "editor" | "form" | "outbox";

const blockers = new Map<symbol, UpdateBlocker>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Block updates until the returned function is called. */
export function blockUpdates(reason: UpdateBlocker): () => void {
  const id = Symbol(reason);
  blockers.set(id, reason);
  emit();
  return () => {
    if (blockers.delete(id)) emit();
  };
}

export function isUpdateBlocked(): boolean {
  return blockers.size > 0;
}

export function updateBlockReasons(): UpdateBlocker[] {
  return [...new Set(blockers.values())];
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUpdateBlocked(): boolean {
  return useSyncExternalStore(subscribe, isUpdateBlocked, isUpdateBlocked);
}

/** Blocks updates while `active` (e.g. a dirty form, the editor with unsaved operations). */
export function useBlockUpdates(active: boolean, reason: UpdateBlocker): void {
  useEffect(() => (active ? blockUpdates(reason) : undefined), [active, reason]);
}

/** Test helper. */
export function resetUpdateGateForTests(): void {
  blockers.clear();
  emit();
}
